import IAmdTimeline from "models/IAmdTimeline";
import ISagaState from "models/ISagaState";
import PipelineFault from "models/PipelineFault";
import ProcessFailure from "models/ProcessFailure";
import type { AmdRetryRow, AmdRow } from "models/AutoRequeueModels";
import { Moment } from "moment";
import BaseService from "services/BaseService";
import { serializeToUtc } from "utils/DateTimeUtils";

export class PipelineService extends BaseService {
    protected urlBase: string = "api/pipeline";

    public async getTimelineByAMD(
        orgId: string,
        amdId: string,
        abortSignal?: AbortSignal,
    ): Promise<IAmdTimeline> {
        const params = new URLSearchParams({
            orgId: orgId,
            amdId: amdId,
        });
        const res = await this.get(
            params,
            "Timeline/GetTimelineByAMDId",
            abortSignal,
        );
        return res;
    }

    public async getFailuresByOrg(
        orgId: string,
        beginDate: Moment,
        endDate: Moment,
    ) {
        const params = new URLSearchParams({
            orgId: orgId,
            beginDate: serializeToUtc(beginDate)!,
            endDate: serializeToUtc(endDate)!,
        });
        const res = await this.get(params, "Faults/GetProcessFailures");
        return res;
    }

    public async getFaults() {
        const params = new URLSearchParams({});
        const res = await this.get(params, "Faults/GetAllFaults");
        return res;
    }

    public async getRbcsToTsQueryFailures() {
        const params = new URLSearchParams({});
        const res = await this.get(params, "RBCTsQueryStatus/GetInvalidRBCs");
        return res;
    }

    public async getAllSagasStateByAMD(
        orgId: string,
        amdId: string,
        abortSignal?: AbortSignal,
    ): Promise<ISagaState[]> {
        const params = new URLSearchParams({
            orgId: orgId,
            amdId: amdId,
        });
        const res = await this.get(
            params,
            "SagaState/GetAllSagasStateByAmd",
            abortSignal,
        );
        return res;
    }

    public async getFaultsByOrg(
        orgId: string,
        beginDate: Moment,
        endDate: Moment,
    ) {
        const params = new URLSearchParams({
            orgId: orgId,
            beginDate: beginDate.format(),
            endDate: endDate.format(),
        });
        const res = await this.get(params, "Faults/GetFaultsByOrganization");
        return res;
    }

    public async getFaultsByOrgandFailure(
        orgId: string,
        failure: ProcessFailure,
    ) {
        const params = new URLSearchParams({
            orgId: orgId,
            amdId: failure.id,
        });
        const res = await this.get(params, "Faults/GetPipelineFaultByAmdId");
        return res;
    }

    public async requeueFaults(orgId: string, faults: PipelineFault[]) {
        const params = new URLSearchParams({ orgId: orgId });
        const res = await this.delete(
            params,
            "Faults/RequeueFaults",
            JSON.stringify(faults),
        );
        return res;
    }

    public async getInteractionsByOrg(
        orgId: string,
        beginDate: Moment,
        endDate: Moment,
    ) {
        const params = new URLSearchParams({
            orgId: orgId,
            beginDate: serializeToUtc(beginDate)!,
            endDate: serializeToUtc(endDate)!,
        });
        const res = await this.get(
            params,
            "SyntheticInteraction/GetInteractionsByOrganization",
        );
        return res;
    }

    public async getRequeuedAMDs(
        take: number = 50,
        minHoursAgo: number = 24,
        minRetryCount: number = 1,
        lastRetriedOn?: string,
        lastId?: string,
    ): Promise<AmdRetryRow[]> {
        const params = new URLSearchParams({
            take: take.toString(),
            minHoursAgo: minHoursAgo.toString(),
            minRetryCount: minRetryCount.toString(),
        });
        if (lastRetriedOn) params.append("lastRetriedOn", lastRetriedOn);
        if (lastId) params.append("lastId", lastId);
        return this.get(params, "Requeuing/GetRequeuedAMDs");
    }

    public async requeueAMDs(
        amdIds: string[],
        requeueToBulkLaneIgnoringHierarchy: boolean = false,
    ): Promise<Response> {
        const params = new URLSearchParams({
            RequeueToBulkLaneIgnoringHierarchy: String(
                requeueToBulkLaneIgnoringHierarchy,
            ),
        });
        return this.post(
            JSON.stringify(amdIds),
            params,
            "Requeuing/RequeueAMDs",
            undefined,
            undefined,
            undefined,
            true,
        );
    }

    public async getProcessingAMDs(
        take: number = 50,
        minHoursAgo: number = 1,
        lastArrivedOn?: string,
        lastId?: string,
    ): Promise<AmdRow[]> {
        const params = new URLSearchParams({
            take: take.toString(),
            minHoursAgo: minHoursAgo.toString(),
        });
        if (lastArrivedOn) params.append("lastArrivedOn", lastArrivedOn);
        if (lastId) params.append("lastId", lastId);
        return this.get(params, "Requeuing/GetProcessingAMDs");
    }

    public async pollOpsDatabase(orgId: string) {
        const params = new URLSearchParams({
            orgId: orgId,
        });
        await this.get(params, "SyntheticInteraction/PollOpsDatabase");
    }

    public async uploadInteractions(orgId: string, fileTypes: string) {
        const params = new URLSearchParams({
            orgId: orgId,
            fileTypes: fileTypes,
        });
        await this.get(params, "SyntheticInteraction/UploadInteractions");
    }
}
